/**
 * Gallery Module
 * Renders and manages wallpaper gallery
 */

import { getAllWallpapers, getWallpapersByCategory, getWallpapersBySubcategory, searchWallpapers, trackDownload, isLiked } from './data.js';
import { getThumbnailUrl, getOriginalUrl } from './imagekitHelper.js';
import router from './router.js';

class Gallery {
  constructor(containerId) {
    this.container = document.getElementById(containerId);
    this.currentFilter = 'all';
    this.currentSubcategory = null;
    this.currentSearch = '';
    this.showFavoritesOnly = false;
    this.currentSort = 'recent'; // Default sort
    this.shuffleOrder = new Map(); // wallpaper.id -> random key, stable until reshuffled
    this._nativeAdInjected = false;
    this.currentPage = 1;
    this.itemsPerPage = 24;
    this._gridResizeTimer = null;

    window.addEventListener('resize', () => {
      clearTimeout(this._gridResizeTimer);
      this._gridResizeTimer = setTimeout(() => this.layoutGalleryGrid(), 120);
    });

    // Listen for favorites filter event
    window.addEventListener('showFavorites', () => {
      this.showFavoritesOnly = true;
      this.currentFilter = 'all';
      this.currentSubcategory = null;
      this.currentSearch = '';
      this.updateGallery();
    });
  }

  setSort(sortType) {
    this.currentSort = sortType;
    this.currentPage = 1;
    this.updateGallery();
  }

  /**
   * Shuffles across the entire filtered set (not just the current page).
   * The order is cached by wallpaper id so paginating afterwards stays
   * stable instead of re-randomizing on every page click. Calling this
   * again produces a brand new random order.
   */
  shuffle() {
    this.shuffleOrder = new Map();
    this.currentSort = 'shuffle';
    this.currentPage = 1;
    this.updateGallery();
  }

  render(wallpapers) {
    if (!this.container) return;

    if (wallpapers.length === 0) {
      this.container.innerHTML = `
        <div style="text-align: center; padding: 3rem; color: var(--color-text-secondary);">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin: 0 auto 1rem;">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <h3>No wallpapers found</h3>
          <p>Try adjusting your search or filter</p>
        </div>
      `;
      return;
    }

    const totalPages = Math.max(1, Math.ceil(wallpapers.length / this.itemsPerPage));
    this.currentPage = Math.min(Math.max(1, this.currentPage), totalPages);

    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const pageWallpapers = wallpapers.slice(startIndex, startIndex + this.itemsPerPage);
    const pageStart = startIndex + 1;
    const pageEnd = startIndex + pageWallpapers.length;

    this.container.innerHTML = `
      <div class="gallery-wallpapers-shell">
        <div class="gallery-results-header">
          <div>
            <h2 class="gallery-results-title">Latest Wallpapers</h2>
            <p class="gallery-results-count">Showing ${pageStart}-${pageEnd} of ${wallpapers.length} wallpapers</p>
          </div>
          <span class="gallery-page-indicator">Page ${this.currentPage} of ${totalPages}</span>
        </div>

        <div class="gallery-masonry-grid">
          ${pageWallpapers.map(w => this.createWallpaperCard(w)).join('')}
        </div>

        ${this.createPagination(totalPages)}
      </div>

      <!-- Native Banner Ad -->
      <div class="native-ad-container">
        <div id="container-41575603342be2dc55c735c9c3eed2d2"></div>
      </div>
    `;

    // Inject native ad script once (re-renders don't need a new script)
    this.injectNativeAd();

    // Add click listeners
    this.attachEventListeners();

    requestAnimationFrame(() => this.layoutGalleryGrid());
  }

  getOrientation(wallpaper) {
    const resolution = wallpaper.resolution || "1920x1080";
    const [width, height] = resolution.split('x').map(Number);
    return width < height ? 'vertical' : 'horizontal';
  }

  createWallpaperCard(wallpaper) {
    const orientation = this.getOrientation(wallpaper);
    const [width, height] = (wallpaper.resolution || '1920x1080').split('x').map(Number);
    const aspectRatio = Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
      ? height / width
      : (orientation === 'vertical' ? 16 / 9 : 9 / 16);
    // Use ImageKit thumbnail URL with selective quality
    const imagePath = getThumbnailUrl(wallpaper.category, wallpaper.filename, orientation === 'vertical');

    // Format subcategory for display
    const subcategoryDisplay = wallpaper.subcategory
      ? wallpaper.subcategory.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      : null;

    return `
      <div class="wallpaper-card card is-${orientation}" 
           data-id="${wallpaper.id}"
           data-aspect-ratio="${aspectRatio}">
        <div class="card-image-container">
          <img
            src="${imagePath}"
            alt="${wallpaper.title}"
            class="wallpaper-card-image"
            loading="lazy"
            onload="this.parentElement.classList.add('loaded')"
          />
          <div class="card-loader"></div>
        </div>
        <div class="wallpaper-card-overlay">
          <h3 class="wallpaper-card-title">${wallpaper.title}</h3>
          <div style="display: flex; gap: 0.5rem; margin-top: 0.5rem; flex-wrap: wrap;">
            <span class="wallpaper-card-category">${wallpaper.category}</span>
            ${subcategoryDisplay ? `<span class="wallpaper-card-category">${subcategoryDisplay}</span>` : ''}
            <span class="wallpaper-card-category">${wallpaper.resolution}</span>
          </div>
        </div>
      </div>
    `;
  }

  layoutGalleryGrid() {
    const grid = this.container?.querySelector('.gallery-masonry-grid');
    if (!grid) return;

    const viewportWidth = window.innerWidth;
    const columnCount = viewportWidth <= 740 ? 2 : (viewportWidth <= 920 ? 6 : 12);
    const gap = 12;
    const columnWidth = (grid.clientWidth - gap * (columnCount - 1)) / columnCount;
    const columnHeights = Array(columnCount).fill(0);

    grid.querySelectorAll('.wallpaper-card').forEach(card => {
      const isHorizontal = card.classList.contains('is-horizontal');
      const columnSpan = columnCount <= 2 ? (isHorizontal ? 2 : 1) : (isHorizontal ? 3 : 2);
      const aspectRatio = parseFloat(card.dataset.aspectRatio) || (isHorizontal ? 9 / 16 : 16 / 9);
      const cardWidth = columnWidth * columnSpan + gap * (columnSpan - 1);
      const cardHeight = cardWidth * aspectRatio;
      let bestStart = 0;
      let bestTop = Infinity;

      for (let start = 0; start <= columnCount - columnSpan; start += 1) {
        const top = Math.max(...columnHeights.slice(start, start + columnSpan));
        if (top < bestTop) {
          bestTop = top;
          bestStart = start;
        }
      }

      const nextHeight = bestTop + cardHeight + gap;
      for (let column = bestStart; column < bestStart + columnSpan; column += 1) {
        columnHeights[column] = nextHeight;
      }

      card.style.width = `${cardWidth}px`;
      card.style.left = `${bestStart * (columnWidth + gap)}px`;
      card.style.top = `${bestTop}px`;
    });

    grid.style.height = `${Math.max(...columnHeights) - gap}px`;
  }

  createPagination(totalPages) {
    if (totalPages <= 1) return '';

    const pages = [];
    const addPage = (page) => {
      pages.push(`
        <button class="pagination-btn ${page === this.currentPage ? 'active' : ''}" data-page="${page}" aria-label="Go to page ${page}">
          ${page}
        </button>
      `);
    };

    addPage(1);
    const start = Math.max(2, this.currentPage - 2);
    const end = Math.min(totalPages - 1, this.currentPage + 2);

    if (start > 2) pages.push('<span class="pagination-ellipsis">...</span>');
    for (let page = start; page <= end; page++) addPage(page);
    if (end < totalPages - 1) pages.push('<span class="pagination-ellipsis">...</span>');
    if (totalPages > 1) addPage(totalPages);

    return `
      <nav class="gallery-pagination" aria-label="Wallpaper pages">
        <button class="pagination-btn pagination-prev" data-page="${this.currentPage - 1}" ${this.currentPage === 1 ? 'disabled' : ''}>
          Prev
        </button>
        <div class="pagination-pages">
          ${pages.join('')}
        </div>
        <button class="pagination-btn pagination-next" data-page="${this.currentPage + 1}" ${this.currentPage === totalPages ? 'disabled' : ''}>
          Next
        </button>
      </nav>
    `;
  }

  injectNativeAd() {
    if (this._nativeAdInjected) return;
    this._nativeAdInjected = true;
    const s = document.createElement('script');
    s.src = 'https://pl28760466.effectivegatecpm.com/41575603342be2dc55c735c9c3eed2d2/invoke.js';
    s.async = true;
    s.setAttribute('data-cfasync', 'false');
    document.head.appendChild(s);
  }

  attachEventListeners() {
    const cards = this.container.querySelectorAll('.wallpaper-card');
    cards.forEach(card => {
      card.addEventListener('click', (e) => {
        if (!e.target.closest('button')) {
          const id = card.dataset.id;
          console.log('Gallery: card clicked, navigating to:', id);
          // Navigate to wallpaper page
          router.navigate(`/wallpaper/${id}`);
        }
      });
    });

    const pageButtons = this.container.querySelectorAll('.pagination-btn[data-page]');
    pageButtons.forEach(button => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        const page = Number(button.dataset.page);
        if (!Number.isFinite(page) || page === this.currentPage) return;
        this.currentPage = page;
        this.updateGallery();
        const filters = document.getElementById('categoryFilter');
        if (filters) filters.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  filterByCategory(category) {
    this.currentFilter = category;
    this.currentSubcategory = null;  // Reset subcategory when changing category
    this.showFavoritesOnly = false;  // Reset favorites filter
    this.currentPage = 1;
    this.updateGallery();
  }

  filterBySubcategory(category, subcategory) {
    this.currentFilter = category;
    this.currentSubcategory = subcategory;
    this.showFavoritesOnly = false;  // Reset favorites filter
    this.currentPage = 1;
    this.updateGallery();
  }

  search(query) {
    this.currentSearch = query;
    this.currentSubcategory = null;  // Reset subcategory when searching
    this.showFavoritesOnly = false;  // Reset favorites filter
    this.currentPage = 1;
    this.updateGallery();
  }

  async updateGallery() {
    let wallpapers;

    if (this.currentSearch) {
      wallpapers = searchWallpapers(this.currentSearch);
    } else if (this.currentSubcategory) {
      wallpapers = getWallpapersBySubcategory(this.currentFilter, this.currentSubcategory);
    } else if (this.currentFilter === 'all') {
      wallpapers = getAllWallpapers();
    } else {
      wallpapers = getWallpapersByCategory(this.currentFilter);
    }

    // Filter to favorites only if that mode is active
    if (this.showFavoritesOnly) {
      const likedStatus = await Promise.all(
        wallpapers.map(w => isLiked(w.id))
      );
      wallpapers = wallpapers.filter((w, i) => likedStatus[i]);
    }

    // Apply sorting
    wallpapers = this.sortWallpapers(wallpapers);

    this.render(wallpapers);
  }

  sortWallpapers(wallpapers) {
    const list = [...wallpapers];

    switch (this.currentSort) {
      case 'rated':
        return list.sort((a, b) => b.rating - a.rating);
      case 'liked':
        return list.sort((a, b) => b.likes - a.likes);
      case 'downloaded':
        return list.sort((a, b) => b.downloads - a.downloads);
      case 'shuffle': {
        // Assign a stable random key per id (lazily, so items revealed by a
        // filter change after shuffling still get placed instead of erroring).
        for (const w of list) {
          if (!this.shuffleOrder.has(w.id)) this.shuffleOrder.set(w.id, Math.random());
        }
        return list.sort((a, b) => this.shuffleOrder.get(a.id) - this.shuffleOrder.get(b.id));
      }
      case 'recent':
      default:
        // Sort by upload date (descending)
        return list.sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));
    }
  }
}

// Global download handler
window.handleDownload = async function (id) {
  const wallpaper = getAllWallpapers().find(w => w.id === id);
  if (!wallpaper) return;

  // Use ImageKit original URL with force download parameter
  let imagePath = getOriginalUrl(wallpaper.category, wallpaper.filename);
  imagePath += (imagePath.includes('?') ? '&' : '?') + 'ik-attachment=true';

  // Create download link
  const link = document.createElement('a');
  link.href = imagePath;
  link.target = '_blank';
  link.download = wallpaper.filename.split('/').pop();
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Track download
  const count = await trackDownload(id);
  console.log(`Downloaded: ${wallpaper.title} (Total: ${count})`);
};

export default Gallery;
